<template>
  <div>
    <FichaEmpresa
      v-if="empresa"
      :empresa="empresa"
      :onBack="() => $router.push('/admin_empresas')"
    >
      <template v-slot:acciones-empresa>
        <v-btn small text color="primary" @click="abrirEdicionEmpresa">
          <v-icon left small>mdi-pencil</v-icon>
          Editar empresa
        </v-btn>
      </template>
      <template v-slot:acciones-sucursal="{ sucursal }">
        <v-btn icon small title="Editar sucursal" @click="abrirEdicionSucursal(sucursal)">
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
      </template>
    </FichaEmpresa>

    <ValidationObserver disabled v-slot="{ invalid }">
      <ActionModal
        :active="!!empresaEditada"
        title="Editar empresa"
        :successAction="guardarEmpresa"
        :cancelAction="() => (empresaEditada = null)"
        :nextDisabled="invalid || guardando"
      >
        <v-container v-if="empresaEditada">
          <TextField
            name="El RUT"
            :isRutEmpresa="true"
            label="Rut de la Empresa: (Ej: XX.XXX.XXX-X)"
            v-model="empresaEditada.rut"
            counter="13"
            rules="required|min:8|max:13|rutempresa"
          />
          <TextField
            name="El nombre"
            label="Nombre de la Empresa:"
            counter="100"
            v-model="empresaEditada.nombre"
            rules="required|min:3|max:100"
          />
        </v-container>
      </ActionModal>
    </ValidationObserver>

    <ValidationObserver disabled v-slot="{ invalid }">
      <ActionModal
        :active="!!sucursalEditada"
        title="Editar sucursal"
        :successAction="guardarSucursal"
        :cancelAction="() => (sucursalEditada = null)"
        :nextDisabled="invalid || guardando"
      >
        <v-container v-if="sucursalEditada">
          <ValidationProvider v-slot="{ errors }" name="El pais" rules="required">
            <v-autocomplete
              :items="paises"
              prepend-inner-icon="domain"
              v-model="sucursalEditada.pais_id"
              item-value="id"
              rounded
              :error-messages="errors"
              item-text="name"
              filled
              label="País"
              @change="cambiarPais"
            ></v-autocomplete>
          </ValidationProvider>
          <ValidationProvider v-slot="{ errors }" name="La region" rules="required">
            <v-autocomplete
              :items="regiones"
              :error-messages="errors"
              prepend-inner-icon="domain"
              v-model="sucursalEditada.region_id"
              item-value="id"
              rounded
              item-text="name"
              filled
              label="Region"
            ></v-autocomplete>
          </ValidationProvider>
          <TextField
            label="Dirección:"
            v-model="sucursalEditada.direccion"
            rules="required|min:5|max:100"
            name="La dirección"
            counter="100"
          />
        </v-container>
      </ActionModal>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import FichaEmpresa from "@/components/empresas/FichaEmpresa.vue";
import ActionModal from "@/components/ActionModal";
import TextField from "@/components/forms/TextField";
import { tablesService } from "@/services";

// La ficha de una empresa para el administrador: además de lo que ve el alumno,
// edita la empresa y sus sucursales. Quién escribió cada comentario lo muestra
// la ficha porque el backend se lo manda solo a un administrador.
export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    FichaEmpresa,
    ActionModal,
    TextField,
  },
  data: () => ({
    empresa: null,
    paises: [],
    regiones: [],
    // { nombre, rut } mientras el modal de la empresa está abierto.
    empresaEditada: null,
    // { id, direccion, pais_id, region_id } mientras el de la sucursal lo está.
    sucursalEditada: null,
    guardando: false,
  }),

  created() {
    tablesService
      .getService(`empresas/${this.$route.params.id}`)
      .then((empresa) => {
        this.empresa = empresa;
      })
      .catch(() => {
        this.$store.dispatch("alert/error", "No se pudo cargar la empresa.");
      });
    tablesService.getService("paises").then((paises) => {
      this.paises = paises;
    });
  },

  methods: {
    abrirEdicionEmpresa() {
      this.empresaEditada = { nombre: this.empresa.nombre, rut: this.empresa.rut };
    },

    guardarEmpresa() {
      this.guardando = true;
      tablesService
        .editService("empresas", `${this.empresa.id}/`, this.empresaEditada)
        .then((empresa) => {
          this.empresa.nombre = empresa.nombre;
          this.empresa.rut = empresa.rut;
          this.empresaEditada = null;
          this.guardando = false;
          this.$store.dispatch("alert/success", "Empresa guardada.");
        })
        .catch((error) => {
          this.guardando = false;
          const datos = error && error.response && error.response.data;
          this.$store.dispatch(
            "alert/error",
            (datos && datos.message) || "No se pudo guardar la empresa."
          );
        });
    },

    cargarRegiones(pais_id) {
      return tablesService.getService("regiones", `?pais=${pais_id}`).then((regiones) => {
        this.regiones = regiones;
      });
    },

    abrirEdicionSucursal(sucursal) {
      this.regiones = [];
      this.cargarRegiones(sucursal.pais.id);
      this.sucursalEditada = {
        id: sucursal.id,
        direccion: sucursal.direccion,
        pais_id: sucursal.pais.id,
        region_id: sucursal.region.id,
      };
    },

    // Las regiones son las del país elegido: al cambiarlo hay que volver a
    // elegir la región.
    cambiarPais() {
      this.sucursalEditada.region_id = null;
      this.cargarRegiones(this.sucursalEditada.pais_id);
    },

    guardarSucursal() {
      const { id, direccion, pais_id, region_id } = this.sucursalEditada;
      this.guardando = true;
      tablesService
        .editService("sucursales", `${id}/`, { direccion, pais_id, region_id })
        .then((sucursal) => {
          const posicion = this.empresa.sucursales.findIndex((item) => item.id === id);
          this.$set(this.empresa.sucursales, posicion, {
            id: sucursal.id,
            direccion: sucursal.direccion,
            region: sucursal.region,
            pais: sucursal.pais,
          });
          this.sucursalEditada = null;
          this.guardando = false;
          this.$store.dispatch("alert/success", "Sucursal guardada.");
        })
        .catch(() => {
          this.guardando = false;
          this.$store.dispatch("alert/error", "No se pudo guardar la sucursal.");
        });
    },
  },
};
</script>

<style></style>
